import { useFeature } from '@growthbook/growthbook-react';
import { fetchRenewalProduct } from '../../../api/policies.api';
import { ProductTypes } from '../../../types';
import type { ProductType } from '../../../types';
import { FEATURE_FLAG_KEYS } from '../../../config/growthbook';
import ProductStatics from '../../../console/_statics/product.statics.json';
import useNotifications from '../../../_lib/notifications/useNotifications';

const getProductDisplayName = (product: string): string => {
  return (
    (ProductStatics.ProductTypes as Record<string, { full?: string }>)?.[
      product
    ]?.full ?? product
  );
};

interface CheckArgs {
  product: ProductType | string | undefined;
  policyId: string | undefined;
}

/**
 * For P250 and P100 policies, verifies via /renewal-product that the policy's
 * selected renewal product has not changed. If it has, surfaces a snackbar and
 * returns false so callers can short-circuit manual renewal flows.
 *
 * - P250: gated by RENEWAL_PRODUCT_SWITCH flag
 * - P100: gated by P100_TO_P100_PRO_RENEWAL_MIGRATION flag
 * - All other products always pass.
 */
export const useCheckP250RenewalEligibility = () => {
  const notifications = useNotifications();
  const renewalProductSwitchFeature = useFeature(
    FEATURE_FLAG_KEYS.RENEWAL_PRODUCT_SWITCH
  );
  const p100MigrationFeature = useFeature(
    FEATURE_FLAG_KEYS.P100_TO_P100_PRO_RENEWAL_MIGRATION
  );

  return async ({ product, policyId }: CheckArgs): Promise<boolean> => {
    const isP250Check =
      renewalProductSwitchFeature.on && product === ProductTypes.p250;
    const isP100Check =
      p100MigrationFeature.on && product === ProductTypes.p100;

    if (!isP250Check && !isP100Check) {
      return true;
    }

    try {
      const { data: renewalProduct } = await fetchRenewalProduct(policyId);
      if (
        renewalProduct?.selectedProduct &&
        renewalProduct.selectedProduct !== product
      ) {
        notifications.error(
          `Policy can not be renewed on ${getProductDisplayName(product as string)}, please renew on ${getProductDisplayName(
            renewalProduct.selectedProduct
          )}`
        );
        return false;
      }
      return true;
    } catch {
      notifications.error(
        'Unable to verify renewal eligibility. Please try again later.'
      );
      return false;
    }
  };
};
